export class HttpError extends Error {
  constructor(
    public readonly statusCode: number,
    message: string,
    options?: ErrorOptions,
  ) {
    super(message, options);
    this.name = 'HttpError';
  }
}

/**
 * Wraps an upstream/external API error so the global error handler can
 * distinguish it from internal service errors (sends 502, reports to Sentry).
 */
export class UpstreamError extends Error {
  readonly statusCode = 502;
  constructor(
    message: string,
    public readonly cause?: unknown,
  ) {
    super(message);
    this.name = 'UpstreamError';
  }
}

/**
 * An *expected, actionable, non-fault* error a plugin MCP tool/resource handler
 * throws to surface a user-state the caller must resolve — e.g.
 * `google_workspace_not_connected: connect a Google Workspace account at
 * /integrations/google-workspace first`. This is a caller-driven condition the
 * handler converts into an MCP `isError` response on purpose (the `getClients`
 * docstring convention): the user gets told what to do, the operator has
 * nothing to triage. Throwing it as a plain `Error` would still surface the
 * `isError` response, but the MCP bridge
 * (`server/src/mcp/plugin-tools.ts`) would also log a
 * `"Plugin MCP tool/resource handler threw"` WARN with the full stack — burying
 * genuine handler faults under expected user-state noise. The bridge
 * special-cases this class exactly as it already special-cases
 * {@link OwnerAuthorizationError} / {@link ResourceOwnerEligibilityError}: it
 * returns `errorResult(toErrorMessage(err))` WITHOUT the WARN, so the WARN
 * stays reserved for genuine handler faults an operator must triage (the
 * existing in-file doctrine). The `isError` response a connected caller sees is
 * byte-identical to throwing a plain `Error`; only the log line is removed.
 *
 * Use it for *expected* user-state only (`*_not_connected`, `*_user_required`,
 * `*_org_required`, …) — never for a wiring/programming fault (a missing
 * service registration, an unexpected upstream 5xx, a TypeError), which must
 * keep logging so the operator can find it. A wiring fault that throws a plain
 * `Error` is the correct default; reserve this class for the actionable
 * user-state the handler already documents as an `isError` outcome.
 */
export class PluginMcpExpectedError extends Error {
  constructor(message: string, options?: ErrorOptions) {
    super(message, options);
    this.name = 'PluginMcpExpectedError';
  }
}

export function toErrorMessage(err: unknown): string {
  if (err instanceof Error) return err.message;
  if (typeof err === 'object' && err !== null) {
    // Non-Error rejections (plain objects from SDKs/APIs) would stringify to
    // "[object Object]" via String(), losing all diagnostic value. Extract a
    // `.message` property if present (common for Error-like API responses),
    // then fall back to JSON.stringify for the rest.
    const msg = (err as { message?: unknown }).message;
    if (typeof msg === 'string' && msg.length > 0) return msg;
    try {
      // `JSON.stringify` returns `undefined` (rather than throwing) when the
      // value's `toJSON()` yields `undefined`; keep the `string` contract.
      const json = JSON.stringify(err);
      if (typeof json === 'string') return json;
    } catch {
      // Circular references or non-serializable values — fall through.
    }
  }
  return String(err);
}

/**
 * True when Runloop rejected a call because the devbox is not in the state the
 * requested action needs — e.g. `400 Devbox is not in proper state for
 * requested action. Current status: DEVBOX_SUSPENDED. Expected:
 * DEVBOX_RUNNING`.
 *
 * Every `execCommand` / `execAsync` against a suspended box fails this way, so
 * any caller that reaches a devbox has to decide what it means rather than
 * letting it fall through as an anonymous failure: it is a state conflict the
 * user can resolve (resume the box), not an internal error and not something
 * retrying can fix — the response even carries `x-should-retry: false`.
 *
 * Matches on "not in proper state" rather than a specific expected status so it
 * covers both directions (an action needing a running box hitting a suspended
 * one, and a resume hitting a box that is already running).
 */
export function isDevboxStateConflictError(err: unknown): boolean {
  if (!err || typeof err !== 'object' || !('status' in err)) return false;
  const apiErr = err as { status: unknown; error?: unknown; message?: unknown };
  if (apiErr.status !== 400) return false;

  const bodyMessage =
    apiErr.error && typeof apiErr.error === 'object' && 'message' in apiErr.error
      ? (apiErr.error as { message: unknown }).message
      : undefined;
  const message =
    typeof bodyMessage === 'string' && bodyMessage.length > 0
      ? bodyMessage
      : typeof apiErr.message === 'string'
        ? apiErr.message
        : '';
  return /not in proper state/i.test(message);
}

/**
 * True when `err` is a Runloop SDK `APIError` that rejected the calling org's
 * stored API key — HTTP 401 (invalid, malformed, or expired key) or 403 (key
 * lacks access). This is the background-seed/read counterpart of the route
 * layer's `authRejected` flag in the server's `extractUpstreamError`: route
 * callers remap such a rejection to a 400 `runloop_key_invalid` for the
 * client, while the background callers here only log. Flagging
 * `authRejected` on those WARN lines lets an operator pull the recurring
 * per-org key-rotation-drift cases out of generic failure noise — a rejected
 * key is actionable (rotate it under Admin → Secrets), a transient 5xx is
 * not (wait) — rather than having to read each stack trace by hand.
 *
 * Keys only on the SDK-set numeric `.status`, which a plain `Error` never
 * carries, so a non-SDK error can never false-positive; a stringified status
 * falls through strict equality. Mirrors the `401 || 403` invariant the
 * route layer uses so the two surfaces agree on what "auth rejected" means.
 */
export function isUpstreamAuthError(err: unknown): boolean {
  if (!err || typeof err !== 'object' || !('status' in err)) return false;
  const apiErr = err as { status: unknown };
  return apiErr.status === 401 || apiErr.status === 403;
}

/**
 * True when `err` is a Runloop SDK `APIError` that rate-limited the calling
 * org's account — HTTP 429. The recurring prod shape is a trial account that
 * has already filled its blueprint quota (`429 Trial account limit reached:
 * You have 5 blueprints (maximum: 5). Please upgrade your account or delete
 * existing blueprints.`), which the org-base bootstrap hits when it tries to
 * create a base blueprint an account at its plan ceiling cannot hold. Like a
 * rejected key, this is a per-org, operator-actionable condition (upgrade the
 * plan or clear blueprints under Admin), not an internal fault and not
 * something the server team can fix — so the background callers that hit it
 * log `rateLimited: true` at WARN rather than ERROR, so per-tenant plan-limit
 * events stop flooding Sentry while still carrying `organizationId` and the
 * upstream request id for triage.
 *
 * A transient `429 Too Many Requests` (upstream throttle, `x-should-retry:
 * true`) also matches: that too is an upstream condition, not a Reflex fault,
 * and WARN is the right severity for it. Keys only on the SDK-set numeric
 * `.status`, which a plain `Error` never carries, so a non-SDK error can
 * never false-positive; a stringified status falls through strict equality,
 * mirroring {@link isUpstreamAuthError}.
 */
export function isUpstreamRateLimitError(err: unknown): boolean {
  if (!err || typeof err !== 'object' || !('status' in err)) return false;
  const apiErr = err as { status: unknown };
  return apiErr.status === 429;
}

/**
 * The Runloop request id stamped on every Runloop SDK `APIError` response
 * header (`x-runloop-request-id`). Surfacing it as a top-level field on the
 * auth/resolve WARN sites (alongside `organizationId` and `authRejected`)
 * lets production-log triage join a Reflex-side rejection to the matching
 * Runloop backend log entry without parsing the nested `err.headers` payload
 * by hand — the request id is the only key the two services share for a
 * given call, so keeping it inline means log-grep correlation needs no DB
 * lookup (the same rationale the `claude-code` rehydrate WARN uses for
 * `agentId`/`devboxId`/`streamId`).
 *
 * Returns the id string, or `undefined` when `err` is not an SDK error or
 * the header is absent (e.g. a non-HTTP failure such as a network timeout
 * carries no response headers). Callers spread the result straight into a
 * pino context object; pino omits `undefined` values, so a missing id
 * simply does not appear in the log rather than serialising as
 * `"runloopRequestId":undefined`.
 *
 * Defensively shaped to mirror {@link isUpstreamAuthError}: an object guard
 * plus a narrow type cast so a non-SDK error (plain `Error`, `null`, a
 * string, an empty object) can never throw and never false-positives.
 */
export function getRunloopRequestId(err: unknown): string | undefined {
  if (!err || typeof err !== 'object') return undefined;
  const headers = (err as { headers?: unknown }).headers;
  if (!headers || typeof headers !== 'object') return undefined;
  const id = (headers as Record<string, unknown>)['x-runloop-request-id'];
  return typeof id === 'string' && id.length > 0 ? id : undefined;
}
